<script setup lang="ts">
import { CustomerDraft } from '@commercetools/platform-sdk';
import keyBy from 'lodash/keyBy';
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { createContact } from '@/api/contacts';
import { RestrictedCountrySkusItem } from '@/api/restrictedCountrySkus';
import Notification from '@/components/base/Notification.vue';
import TextButton from '@/components/base/TextButton.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import GuestEmailEntry from '@/components/checkout/GuestEmailEntry.vue';
import CountryRestrictedItemsNotification from '@/components/checkout/steps/address/CountryRestrictedItemsNotification.vue';
import Allocation from '@/components/checkout/steps/multiship/Allocation.vue';
import RemoveAddress from '@/components/checkout/steps/multiship/RemoveAddress.vue';
import SavedShipment from '@/components/checkout/steps/multiship/SavedShipment.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { useDelivery } from '@/composables/useDelivery';
import { useAddressFields } from '@/composables/useForm';
import { NutsLineItem } from '@/lib/cart/lineItem';
import { NutsAddress } from '@/utils/address';
import { isGiftLineItem } from '@/utils/cart';

export interface EditableLineItem extends NutsLineItem {
  originalQuantity: number;
  quantityAvailable: number;
}

export interface EditableAllocation {
  address: NutsAddress;
  key?: string;
  lineItems: EditableLineItem[];
}

const props = defineProps<{
  addresses?: NutsAddress[];
  countryRestrictedItems?: RestrictedCountrySkusItem;
  customer?: CustomerDraft;
}>();

const emit = defineEmits<{
  'step-complete': [];
}>();

const store = useStore();
const { initializeAddress } = useAddressFields();
const { cxMode, isB2B } = useCheckout(store);
const {
  customerEmail,
  lineItems: cartLineItems,
  setCustomerEmail,
  unassignedLineItems,
} = useCart(store);
const {
  addShipment,
  nextMultishipSerial,
  removeShipment,
  standardShipments,
  updateRemainingQuantity,
  updateShipment,
} = useDelivery(store);
const router = useRouter();

const allocation = ref<EditableAllocation>({
  address: initializeAddress(props.addresses?.[0]),
  key: undefined,
  lineItems: [],
});

const allocationFormElement = ref<HTMLElement>();

const allRestricted = ref(false);
const currentRestrictedItems = ref<NutsLineItem[]>([]);
const currentRestrictedSkus = computed(() =>
  currentRestrictedItems.value.map((item) => item.variant.sku),
);

const giftLineItems = computed(() => cartLineItems.value.filter(isGiftLineItem));

const guestEmail = ref({
  address: customerEmail.value ?? '',
});

const pendingItemsToAbandon = ref();

const addressNumber = computed(() => {
  if (!allocation.value.key) {
    return nextMultishipSerial.value;
  }
  const shipment = standardShipments.value.find((s) => s.key === allocation.value.key);
  return shipment?.multishipSerial ?? nextMultishipSerial.value;
});

const showEmailEditable = computed<boolean>(() => !props.customer && addressNumber.value === 1);

const combineLineItemOptions = (savedLineItems?: NutsLineItem[]) => {
  const lineItems: EditableLineItem[] =
    savedLineItems?.map((item) => ({
      ...item,
      originalQuantity: item.quantity,
      quantityAvailable: item.quantity,
    })) ?? [];

  const lineItemsById = keyBy(lineItems, (item) => item.id);
  unassignedLineItems.value.forEach((item) => {
    if (item.id in lineItemsById) {
      lineItemsById[item.id].quantityAvailable += item.quantity;
    } else {
      lineItemsById[item.id] = {
        ...item,
        originalQuantity: 0,
        quantity: 0,
        quantityAvailable: item.quantity,
      };
    }
  });

  return Object.values(lineItemsById);
};

const removalPending = ref(false);
const showAddressRemovedNotice = ref(false);
const showAllocationForm = ref(
  unassignedLineItems.value.length > 0 || !!allocation.value.address?.key,
);

const setAllocation = (shipmentKey?: string) => {
  let savedAllocation;
  if (shipmentKey) {
    savedAllocation = standardShipments.value.find((s) => s.key === shipmentKey);
  }
  allocation.value = {
    address: initializeAddress(savedAllocation?.address || props.addresses?.[0]),
    key: shipmentKey,
    lineItems: combineLineItemOptions(savedAllocation?.lineItems),
  };

  showAddressRemovedNotice.value = false;
};

setAllocation();

const onEditShipment = async (shipmentKey: string) => {
  showAllocationForm.value = false;
  await nextTick();
  setAllocation(shipmentKey);
  await nextTick();
  showAllocationForm.value = true;
};

const onRemoveShipment = async () => {
  removalPending.value = true;
  try {
    await removeShipment(allocation.value.key!);
    showAllocationForm.value = false;
    await nextTick();
    setAllocation();
    await nextTick();
    showAddressRemovedNotice.value = true;
  } catch (e) {
    console.error(e);
  }
  showAllocationForm.value = true;
  removalPending.value = false;
};

const stepComplete = () => {
  emit('step-complete');
};

const showRestrictedItemsNotification = ref(false);

const containsRestrictedItems = () => {
  const { countryRestrictedItems } = props;
  const { address } = allocation.value;

  if (address.country === 'US' || !countryRestrictedItems) return false;

  const restrictedSkus = Object.keys(countryRestrictedItems);

  allocation.value.lineItems
    .filter((lineItem) => lineItem.quantity > 0)
    .forEach((lineItem) => {
      const { sku } = lineItem.variant;
      if (
        !currentRestrictedSkus.value.includes(sku) &&
        restrictedSkus.includes(sku) &&
        countryRestrictedItems[sku].includes(address.country)
      ) {
        currentRestrictedItems.value.push(lineItem);
      }
    });

  allRestricted.value = allocation.value.lineItems.length === currentRestrictedItems.value.length;

  return currentRestrictedItems.value.length > 0;
};

const save = async (itemsToAbandon?: EditableLineItem[]) => {
  if (containsRestrictedItems()) {
    showRestrictedItemsNotification.value = true;
    pendingItemsToAbandon.value = itemsToAbandon;
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  } else {
    if (addressNumber.value === 1 && guestEmail.value.address) {
      const b2bSource = cxMode.value
        ? 'cx checkout buying for business'
        : 'checkout buying for business';
      createContact({
        email: guestEmail.value.address,
        subscribed: 'if-new',
        b2b: isB2B.value,
        b2bSource: isB2B.value ? b2bSource : undefined,
      });
      await setCustomerEmail(guestEmail.value.address);
    }

    const { address, lineItems } = allocation.value;

    const lineQuantities = lineItems.reduce<{ [id: string]: number }>(
      (keyedQuantities, lineItem) => {
        if (!lineItem.quantity) return keyedQuantities;
        return {
          ...keyedQuantities,
          [lineItem.id]: lineItem.quantity,
        };
      },
      {},
    );

    if (address?.key) {
      await updateShipment(address.key, address, lineQuantities);
    } else if (address && Object.keys(lineQuantities).length) {
      await addShipment(address, lineQuantities);
    }

    if (itemsToAbandon) {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of itemsToAbandon) {
        // eslint-disable-next-line no-await-in-loop
        await updateRemainingQuantity(item.id, 0);
      }
    }

    if (cartLineItems.value.length === 0) {
      await router.push('/cart');
      return;
    }

    if (unassignedLineItems.value.length === 0) {
      stepComplete();
    } else {
      showAllocationForm.value = false;
      await nextTick();
      setAllocation();
      await nextTick();
      showAllocationForm.value = true;

      await nextTick();
      allocationFormElement.value?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }
};

const addAnotherShipment = useCallback(save);
const continueWithShipments = useCallback(save);

const onSaveAndContinue = async ({
  buttonName,
  itemsToAbandon,
}: {
  buttonName?: string;
  itemsToAbandon?: EditableLineItem[];
}) => {
  switch (buttonName) {
    case 'addAnother':
      addAnotherShipment.execute();
      break;
    case 'continue':
      continueWithShipments.execute(itemsToAbandon);
      break;
    default:
      save(itemsToAbandon);
      break;
  }
};

const onRemoveRemainingItems = async (itemsToAbandon: EditableLineItem[]) => {
  onSaveAndContinue({ buttonName: 'continue', itemsToAbandon });
};

const onRemoveRestrictedItems = () => {
  allocation.value.lineItems = allocation.value.lineItems.filter(
    (lineItem) => !currentRestrictedSkus.value.includes(lineItem.variant.sku),
  );
  showRestrictedItemsNotification.value = false;
  currentRestrictedItems.value = [];

  onSaveAndContinue({ itemsToAbandon: pendingItemsToAbandon.value });
};

watch(
  giftLineItems,
  (newLines, oldLines) => {
    if (newLines.length >= oldLines.length) return;
    const newIds = newLines.map((l) => l.id);
    const removedIds = oldLines.map((l) => l.id).filter((id) => !newIds.includes(id));
    allocation.value.lineItems = allocation.value.lineItems.filter(
      (l) => !removedIds.includes(l.id),
    );
  },
  { deep: true },
);

watch(
  unassignedLineItems,
  () => {
    if (!allocation.value.lineItems.length) return;
    const lineItemsById = keyBy(allocation.value.lineItems, (item) => item.id);
    unassignedLineItems.value.forEach((item) => {
      const lineItem = lineItemsById[item.id];
      if (lineItem) {
        lineItem.quantityAvailable = item.quantity + lineItem.originalQuantity;
      } else {
        allocation.value.lineItems.push({
          ...item,
          quantity: 0,
          originalQuantity: 0,
          quantityAvailable: item.quantity,
        });
      }
    });
    const unassignedLineItemIds = unassignedLineItems.value.map((i) => i.id);
    Object.entries(lineItemsById)
      .filter(([id]) => !unassignedLineItemIds.includes(id))
      .forEach(([id, item]) => {
        if (item.originalQuantity) {
          const lineItem = lineItemsById[id];
          lineItem.quantityAvailable = item.originalQuantity;
        } else {
          const { lineItems } = allocation.value;
          lineItems.splice(
            lineItems.findIndex((i) => i.id === id),
            1,
          );
        }
      });
  },
  { deep: true },
);
</script>

<template>
  <div data-test="multi-ship">
    <div class="divide-y divide-gray-200 divide-solid">
      <div
        v-if="!customer && customerEmail && !showEmailEditable"
        class="pt-4 mb-4 md:pt-5 md:mb-5"
      >
        <div class="flex content-center justify-between w-full mb-2">
          <strong class="text-base leading-5 md:text-lg" data-test="receipt-email">
            Email for Order Updates
          </strong>
          <TextButton @click="onEditShipment(standardShipments[0].key)">Edit</TextButton>
        </div>
        <div class="text-sm leading-4 md:leading-5 md:text-base">
          {{ customerEmail }}
        </div>
      </div>

      <SavedShipment
        v-for="shipment in standardShipments"
        :key="shipment.key"
        class="pt-4 mb-4 md:pt-5 md:mb-5"
        editable
        :addressNumber="shipment.multishipSerial"
        :shipment
        @edit-shipment="onEditShipment"
      />

      <transition name="fade" mode="out-in">
        <div v-if="showAllocationForm">
          <transition name="fade">
            <CountryRestrictedItemsNotification
              v-if="showRestrictedItemsNotification"
              class="mt-3 md:mt-6"
              :allRestricted
              :restrictedItems="currentRestrictedItems"
              @remove-items="onRemoveRestrictedItems"
            />
          </transition>

          <div ref="allocationFormElement" />
          <Allocation
            v-model:address="allocation.address"
            v-model:lineItems="allocation.lineItems"
            :key="allocation.key"
            class="mt-7"
            :addresses
            :addressNumber
            :customer
            :isAddAnotherPending="addAnotherShipment.isPending"
            :isContinuePending="continueWithShipments.isPending"
            :isRemovalPending="removalPending"
            @remove-remaining-items="onRemoveRemainingItems"
            @save-and-continue="onSaveAndContinue"
          >
            <template v-slot:email>
              <div v-if="showEmailEditable" class="mt-5 md:mt-7">
                <strong class="text-base leading-5 md:text-lg"> Email for Order Updates </strong>
                <GuestEmailEntry v-model="guestEmail" :stepNumber="2" class="mt-2.5" />
              </div>
            </template>
            <template v-slot:removal>
              <transition name="fade">
                <RemoveAddress
                  v-if="!!allocation.key"
                  :removalPending
                  @remove-shipment="onRemoveShipment"
                />
              </transition>
            </template>
            <template v-slot:removed>
              <transition name="fade">
                <Notification v-if="showAddressRemovedNotice" class="mt-4 mb-6">
                  Okay, we’ve cleared that address and its contents. Below is what’s left for you to
                  send.
                </Notification>
              </transition>
            </template>
          </Allocation>
        </div>
        <div v-else>
          <ThemedButton class="mt-5 md:mt-7" type="button" theme="gray" @click="stepComplete">
            Continue
          </ThemedButton>
        </div>
      </transition>
    </div>
  </div>
</template>
